import React, { useState } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Menu, X, ChevronDown, LogOut, User, BookOpen, FileText, LayoutDashboard } from 'lucide-react';

const ROLE_LABELS = {
  author: 'Author',
  reviewer: 'Reviewer',
  admin: 'Editor',
  superadmin: 'Super Admin',
};

const Navbar = () => {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);
  const [dropdownOpen, setDropdownOpen] = useState(false);

  const handleLogout = () => {
    logout();
    setDropdownOpen(false);
    setMobileOpen(false);
    navigate('/');
  };

  const isActive = (path) => location.pathname === path || location.pathname.startsWith(path + '/');

  const navLinks = [
    { label: 'Home', path: '/' },
    { label: 'Journals', path: '/journals' },
    { label: 'Editorial Board', path: '/editorial-board' },
  ];

  return (
    <header className="bg-white border-b border-[#E5E7EB] sticky top-0 z-50" data-testid="navbar">
      {/* Top brand strip */}
      <div className="border-b border-[#E5E7EB] bg-[#F5F7FA] py-1.5 hidden md:block">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between">
          <span className="text-xs text-[#6B7280] tracking-wide">
            A Publication of MAH Quantum Research Institute
          </span>
          <span className="text-xs text-[#6B7280]">
            For submissions &amp; inquiries: <span className="text-[#1A73E8]">submissions@mahquantum.tech</span>
          </span>
        </div>
      </div>

      {/* Main nav */}
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center justify-between h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 flex-shrink-0" data-testid="navbar-logo">
            <img src="/mah-logo.png" alt="MAH Quantum Research Institute" className="w-9 h-9 object-cover rounded" />
            <div className="hidden sm:block">
              <div className="text-[#1F1F1F] font-serif font-semibold text-base leading-tight tracking-tight">
                MAH Quantum Research Institute
              </div>
              <div className="text-[#6B7280] text-xs tracking-wide">
                Academic &amp; Professional Publishing Platform
              </div>
            </div>
          </Link>

          {/* Desktop Nav Links */}
          <nav className="hidden md:flex items-center gap-1" data-testid="desktop-nav">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  isActive(link.path) && link.path !== '/'
                    ? 'text-[#1A73E8] border-b-2 border-[#1A73E8]'
                    : location.pathname === '/' && link.path === '/'
                    ? 'text-[#1A73E8] border-b-2 border-[#1A73E8]'
                    : 'text-[#4B5563] hover:text-[#1F1F1F]'
                }`}
                data-testid={`nav-link-${link.label.toLowerCase().replace(' ', '-')}`}
              >
                {link.label}
              </Link>
            ))}
            {user && (
              <Link
                to="/dashboard"
                className={`px-4 py-2 text-sm font-medium transition-colors ${
                  isActive('/dashboard') ? 'text-[#1A73E8] border-b-2 border-[#1A73E8]' : 'text-[#4B5563] hover:text-[#1F1F1F]'
                }`}
                data-testid="nav-link-dashboard"
              >
                Dashboard
              </Link>
            )}
          </nav>

          {/* Desktop Auth */}
          <div className="hidden md:flex items-center gap-2" data-testid="desktop-auth">
            {user ? (
              <div className="relative">
                <button
                  onClick={() => setDropdownOpen(!dropdownOpen)}
                  className="flex items-center gap-2 px-4 py-2 border border-[#E5E7EB] text-sm text-[#1F1F1F] hover:bg-[#F5F7FA] transition-colors"
                  data-testid="user-menu-button"
                >
                  <div className="w-6 h-6 bg-[#1A73E8] flex items-center justify-center">
                    <span className="text-white text-xs font-semibold">{user.name.charAt(0)}</span>
                  </div>
                  <span className="max-w-[120px] truncate">{user.name.split(' ').pop()}</span>
                  <span className="text-xs text-[#6B7280] bg-[#F5F7FA] px-1.5 py-0.5 border border-[#E5E7EB]">
                    {ROLE_LABELS[user.role]}
                  </span>
                  <ChevronDown size={14} className="text-[#6B7280]" />
                </button>
                {dropdownOpen && (
                  <div className="absolute right-0 top-full mt-1 w-52 bg-white border border-[#E5E7EB] shadow-lg z-50" data-testid="user-dropdown">
                    <div className="px-4 py-3 border-b border-[#E5E7EB]">
                      <p className="text-sm font-semibold text-[#1F1F1F] truncate">{user.name}</p>
                      <p className="text-xs text-[#6B7280] truncate">{user.email}</p>
                    </div>
                    <Link
                      to="/dashboard"
                      onClick={() => setDropdownOpen(false)}
                      className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#374151] hover:bg-[#F5F7FA] transition-colors"
                      data-testid="dropdown-dashboard"
                    >
                      <LayoutDashboard size={14} /> Dashboard
                    </Link>
                    {(user.role === 'author' || user.role === 'superadmin') && (
                      <Link
                        to="/submit"
                        onClick={() => setDropdownOpen(false)}
                        className="flex items-center gap-2 px-4 py-2.5 text-sm text-[#374151] hover:bg-[#F5F7FA] transition-colors"
                        data-testid="dropdown-submit"
                      >
                        <FileText size={14} /> Submit Paper
                      </Link>
                    )}
                    <button
                      onClick={handleLogout}
                      className="w-full flex items-center gap-2 px-4 py-2.5 text-sm text-[#DC2626] hover:bg-red-50 transition-colors border-t border-[#E5E7EB] mt-1"
                      data-testid="logout-button"
                    >
                      <LogOut size={14} /> Sign Out
                    </button>
                  </div>
                )}
              </div>
            ) : (
              <>
                <Link
                  to="/login"
                  className="px-4 py-2 text-sm font-medium text-[#4B5563] hover:text-[#1F1F1F] transition-colors"
                  data-testid="login-nav-link"
                >
                  Sign In
                </Link>
                <Link
                  to="/register"
                  className="px-4 py-2 bg-[#1A73E8] text-white text-sm font-medium hover:bg-[#174EA6] transition-colors"
                  data-testid="register-nav-link"
                >
                  Register
                </Link>
              </>
            )}
          </div>

          {/* Mobile toggle */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className="md:hidden p-2 text-[#4B5563] hover:text-[#1F1F1F]"
            data-testid="mobile-menu-toggle"
          >
            {mobileOpen ? <X size={20} /> : <Menu size={20} />}
          </button>
        </div>
      </div>

      {/* Mobile menu */}
      {mobileOpen && (
        <div className="md:hidden border-t border-[#E5E7EB] bg-white" data-testid="mobile-menu">
          <nav className="px-4 py-3 space-y-1">
            {navLinks.map(link => (
              <Link
                key={link.path}
                to={link.path}
                onClick={() => setMobileOpen(false)}
                className="block px-3 py-2 text-sm text-[#374151] hover:bg-[#F5F7FA]"
              >
                {link.label}
              </Link>
            ))}
            {user && (
              <Link to="/dashboard" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-[#374151] hover:bg-[#F5F7FA]">
                Dashboard
              </Link>
            )}
            <div className="border-t border-[#E5E7EB] pt-2 mt-2">
              {user ? (
                <>
                  <div className="px-3 py-2">
                    <p className="text-sm font-semibold">{user.name}</p>
                    <p className="text-xs text-[#6B7280]">{ROLE_LABELS[user.role]}</p>
                  </div>
                  <button onClick={handleLogout} className="w-full text-left px-3 py-2 text-sm text-[#DC2626] hover:bg-red-50">
                    Sign Out
                  </button>
                </>
              ) : (
                <>
                  <Link to="/login" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-[#374151] hover:bg-[#F5F7FA]">
                    Sign In
                  </Link>
                  <Link to="/register" onClick={() => setMobileOpen(false)} className="block px-3 py-2 text-sm text-[#1A73E8] hover:bg-[#F5F7FA]">
                    Register
                  </Link>
                </>
              )}
            </div>
          </nav>
        </div>
      )}
    </header>
  );
};

export default Navbar;
